import React, { useState } from 'react';

interface Split {
  km: number;
  time: number;
  pace: number;
  type?: 'auto' | 'manual';
  heartRate?: number;
}

interface ExpandableSplitsProps {
  splits: Split[];
}

export const ExpandableSplits: React.FC<ExpandableSplitsProps> = ({ splits }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatPace = (secondsPerKm: number): string => {
    if (!secondsPerKm || !isFinite(secondsPerKm)) return '--:--';
    const minutes = Math.floor(secondsPerKm / 60);
    const seconds = Math.floor(secondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const avgPace = splits.reduce((acc, split) => acc + split.pace, 0) / splits.length;
  const fastestSplit = splits.reduce((fastest, split) => 
    split.pace < fastest.pace ? split : fastest
  , splits[0]);
  const slowestSplit = splits.reduce((slowest, split) => 
    split.pace > slowest.pace ? split : slowest
  , splits[0]);

  return (
    <details 
      className="w-full max-w-2xl mx-auto mb-4"
      open={isExpanded}
      onToggle={(e) => setIsExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-center text-sm font-medium text-blue-400 hover:text-blue-300 py-2 px-4 bg-gray-800 rounded-lg transition-colors">
        Show Splits ({splits.length}) ▼
      </summary>
      
      <div className="mt-3 bg-gray-800 rounded-lg p-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-700">
          <div className="text-center">
            <div className="text-xs text-gray-400 mb-1">Average Pace</div>
            <div className="text-lg font-bold text-white">{formatPace(avgPace)}</div>
            <div className="text-xs text-gray-500">/km</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400 mb-1">Fastest Split</div>
            <div className="text-lg font-bold text-green-400">{formatPace(fastestSplit.pace)}</div>
            <div className="text-xs text-gray-500">km {fastestSplit.km}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400 mb-1">Slowest Split</div>
            <div className="text-lg font-bold text-red-400">{formatPace(slowestSplit.pace)}</div>
            <div className="text-xs text-gray-500">km {slowestSplit.km}</div>
          </div>
        </div>
        
        {/* Splits Table */}
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-5 gap-2 text-xs text-gray-400 font-medium px-2">
            <div>Split</div>
            <div className="text-right">Time</div>
            <div className="text-right">Pace</div>
            <div className="text-right">+/−</div>
            {splits.some(s => s.heartRate) && <div className="text-right">HR</div>}
          </div>
          
          {/* Splits */}
          {splits.map((split, idx) => {
            const diff = split.pace - avgPace;
            const isFastest = split.km === fastestSplit.km;
            const isSlowest = split.km === slowestSplit.km;
            
            return (
              <div 
                key={idx}
                className={`grid grid-cols-5 gap-2 text-sm px-2 py-2 rounded ${
                  isFastest ? 'bg-green-900/20' : 
                  isSlowest ? 'bg-red-900/20' : 
                  'hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-1 text-white">
                  {split.type === 'manual' && <span className="text-xs">📍</span>}
                  <span>{split.type === 'manual' ? `Lap ${idx + 1}` : `${split.km} km`}</span>
                </div>
                <div className="text-right text-gray-300 font-mono">{formatTime(split.time)}</div>
                <div className={`text-right font-mono font-bold ${
                  isFastest ? 'text-green-400' :
                  isSlowest ? 'text-red-400' :
                  'text-blue-400'
                }`}>
                  {formatPace(split.pace)}
                </div>
                <div className={`text-right text-xs ${
                  diff < 0 ? 'text-green-400' : diff > 0 ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {diff !== 0 && (diff < 0 ? '−' : '+')}
                  {Math.abs(Math.round(diff))}s
                </div>
                {split.heartRate && (
                  <div className="text-right text-gray-300">{split.heartRate}</div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Consistency Score */}
        <div className="mt-4 pt-4 border-t border-gray-700 text-center">
          <div className="text-xs text-gray-400 mb-1">Pace Consistency</div>
          <div className="text-sm text-gray-300">
            {splits.filter(s => Math.abs(s.pace - avgPace) <= 5).length} / {splits.length} splits within 5s of average
          </div>
        </div>
      </div>
    </details>
  );
};

